import {inject, Injectable} from '@angular/core';
import {AuthQuery} from '../../../queries/auth.query';
import {
    AMAGNO_FILE_EXTENSION,
    IPC_APP_BEFORE_QUIT,
    IPC_APP_BOOTSTRAPPED,
    IPC_APP_READY_SIGNAL,
    IPC_AUTH_TOKEN_CHANGED,
    IPC_GLOBAL_DOCUMENT_DOWNLOAD_DONE,
    IPC_GLOBAL_DOCUMENT_DOWNLOAD_ERROR,
    IPC_GLOBAL_SHORTCUT,
    IPC_IMPORT_ERROR,
    IPC_IMPORT_FILE,
    IPC_OPEN_AMAGNO_FILE,
    IPC_OPEN_DEEP_LINK,
    IPC_OPEN_FILE,
    PROCESS_FILE_ARGUMENT
} from '../../../constants/shared.constants';
import {AppService} from '../../app/app.service';
import {SnackbarService} from '../../snackbar/snackbar.service';
import {ImportService} from '../../import/import.service';
import {AuthService} from '../../auth/auth.service';
import {AmagnoLinkService} from '../../amagno-link/amagno-link.service';
import {environment} from '../../../../environments/environment';
import {UploadFile} from '../../../models/import/upload-file.model';
import {ElectronService} from '../electron.service';
import {TranslationKey} from '../../../types/available-translations.type';
import {SearchService} from '../../search/search.service';

@Injectable({
    providedIn: 'root',
})
export class ElectronAppService {
    private readonly appService = inject(AppService);
    private readonly authQuery = inject(AuthQuery);
    private readonly authService = inject(AuthService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly amagnoLinkService = inject(AmagnoLinkService);
    private readonly importService = inject(ImportService);
    private readonly searchService = inject(SearchService);
    private lastRequestedDocumentName: string = '';

    public setLastRequestedDocumentName(name: string): void {
        this.lastRequestedDocumentName = name;
    }

    public registerDeepLinkListener(): void {
        ElectronService.ipcRender?.receive(
            IPC_OPEN_DEEP_LINK,
            this.appService.handleExternalEvent((urlArg: unknown) => {
                const url = urlArg as string;
                // Because file-import events are also recognized as deep-link events, due to a reserved
                // event name, ignore events that contains the comma-separated "process file" argument, or
                // Windows App events that contains an Amagno file.
                if ((url.includes(PROCESS_FILE_ARGUMENT + ',')) || (ElectronService.isWindows() && url.endsWith(AMAGNO_FILE_EXTENSION))) {
                    return;
                }

                this.authService.continueAfterLoggedIn(async (): Promise<boolean> => {
                    await this.globalShortcutCalled(url);
                    return true;
                });
            }) as (...args: Array<unknown>) => void
        );
    }

    public registerOpenFileListener(): void {
        ElectronService.ipcRender?.receive(
            IPC_OPEN_FILE,
            this.appService.handleExternalEvent(() => {
                this.importFiles();
            }) as (...args: Array<unknown>) => void
        );
    }

    public registerFileImportListener(): void {
        ElectronService.ipcRender?.receive(
            IPC_IMPORT_FILE,
            this.appService.handleExternalEvent((filesData: unknown) => {
                const files = filesData as Array<{ filePath: string, fileName: string, fileData: BlobPart, birthtimeInMS: number }>;
                this.authService.continueAfterLoggedIn((): Promise<boolean> => {
                    if (this.importService.importDialogOpen() && this.importService.hasFiles()) {
                        this.snackbarService.showError('IMPORT.ALREADY_STARTED_CONFIRM');
                        return Promise.resolve(false);
                    }

                    const importFiles = [];
                    for (const file of files) {
                        const importFile = new File([file.fileData], file.fileName) as UploadFile;
                        importFile.filePath = file.filePath;
                        importFile.creationDate = Math.floor(file.birthtimeInMS);
                        importFiles.push(importFile);
                    }
                    this.importService.showImportDialog(importFiles);
                    return Promise.resolve(true);
                });
            }) as (...args: Array<unknown>) => void
        );

        ElectronService.ipcRender?.receive(
            IPC_OPEN_AMAGNO_FILE,
            this.appService.handleExternalEvent((filesData: unknown) => {
                this.authService.continueAfterLoggedIn((): Promise<boolean> => {
                    const files = filesData as Array<{ filePath: string, fileName: string, fileData: BlobPart, birthtimeInMS: number }>;
                    const file = files.shift();

                    if (!file) {
                        this.snackbarService.showError('ERROR.FILE.READ');
                        return Promise.resolve(false);
                    }

                    try {
                        const fileReader = new FileReader();
                        const amagnoFile = new File([file.fileData], file.fileName);

                        fileReader.addEventListener('load', async () => {
                            const result = fileReader.result;

                            if (result) {
                                await this.amagnoLinkService.openAmagnoLinkByAmagnoFileContent(result as string);
                            } else {
                                this.snackbarService.showError('ERROR.FILE.READ');
                            }
                        }, false);

                        fileReader.readAsText(amagnoFile);

                        return Promise.resolve(true);
                    } catch (error) {
                        console.error(error);
                        this.snackbarService.showError('ERROR.FILE.READ');

                        return Promise.resolve(false);
                    }
                });
            }) as (...args: Array<unknown>) => void
        );

        ElectronService.ipcRender?.receive(
            IPC_IMPORT_ERROR,
            this.appService.handleExternalEvent((eventData: unknown) => {
                const [errorMsgKey, error, filename] = eventData as [TranslationKey, Error | undefined, string | undefined];

                if (error) {
                    this.snackbarService.showError(errorMsgKey, error as Error, { filename });
                } else {
                    this.snackbarService.showInfo(errorMsgKey);
                }
            }) as (...args: Array<unknown>) => void
        );
    }

    public sendReadySignal(): void {
        ElectronService.ipcRender?.send(IPC_APP_READY_SIGNAL);
    }

    public sendBootstrappedSignal(): void {
        ElectronService.ipcRender?.send(IPC_APP_BOOTSTRAPPED);
    }

    public tryInitElectronApp(): boolean {
        if (!ElectronService.isElectronApp()) {
            return false;
        }

        this.authQuery.token$.subscribe(token => {
            ElectronService.ipcRender?.send<string>(IPC_AUTH_TOKEN_CHANGED, token);
        });

        ElectronService.ipcRender?.receive(
            IPC_GLOBAL_SHORTCUT,
            this.appService.handleExternalEvent((a: unknown) => this.globalShortcutCalled(a as string)) as (...args: Array<unknown>) => void
        );

        ElectronService.ipcRender?.receive(
            IPC_GLOBAL_DOCUMENT_DOWNLOAD_DONE,
            this.appService.handleExternalEvent(() => {
                if (this.lastRequestedDocumentName) {
                    this.snackbarService.showSuccess('EXPORT_DOCUMENT_SUCCESS', { document: this.lastRequestedDocumentName });
                    this.lastRequestedDocumentName = '';
                }
            }) as (...args: Array<unknown>) => void
        );

        ElectronService.ipcRender?.receive(
            IPC_GLOBAL_DOCUMENT_DOWNLOAD_ERROR,
            this.appService.handleExternalEvent(() => {
                if (this.lastRequestedDocumentName) {
                    this.snackbarService.showError('FILE_DOWNLOAD_ERROR_MSG');
                    this.lastRequestedDocumentName = '';
                }
            }) as (...args: Array<unknown>) => void
        );

        return true;
    }

    public beforeAppQuit(func: () => unknown): void {
        ElectronService.ipcRender?.receive(IPC_APP_BEFORE_QUIT, func);
    }

    private importFiles(): void {
        this.authService.continueAfterLoggedIn((): Promise<boolean> => {
            if (this.importService.importDialogOpen() && this.importService.hasFiles()) {
                this.snackbarService.showError('IMPORT.ALREADY_STARTED_CONFIRM');
                return Promise.resolve(false);
            }

            let error: Error | undefined;
            //TODO: ELECTRON put this into electron itself or use capacitor
            const hasFolderError = true;
            const files: Array<UploadFile> = [];

            if (hasFolderError) {
                this.snackbarService.showInfo('IMPORT.ERROR_FOLDERS_CURRENTLY_NOT_SUPPORTED');
                return Promise.resolve(false);
            }

            if (error) {
                this.snackbarService.showError('ERROR_READING_FILE', error as Error);
                return Promise.resolve(false);
            }

            this.importService.showImportDialog(files);
            return Promise.resolve(true);
        });
    }

    private async globalShortcutCalled(clipboardText: string): Promise<void> {
        if (clipboardText.startsWith(environment.deepLinkPrefix) || clipboardText.startsWith('url:' + environment.deepLinkPrefix)) {
            await this.amagnoLinkService.openAmagnoLinkByAmagnoDeepLink(clipboardText);
        } else {
            await this.searchService.openSearch(clipboardText);
        }
    }
}
